package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.dom.observers.ResizeObserver
import dev.lingvo.shared.screenshots.ScreenshotEngine
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.geom.Guide
import dev.lingvo.shared.screenshots.geom.Handle
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.TextMeasurer
import dev.lingvo.shared.screenshots.scene.SceneArranger
import dev.lingvo.site.components.studio.canvas.CanvasMeasurer
import dev.lingvo.site.components.studio.canvas.CanvasSurface
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlin.js.Date
import kotlin.math.atan2
import kotlin.math.roundToInt
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import dev.lingvo.site.i18n.t

/**
 * The stage owns DOM resources, selection, viewport and a plain transient gesture. Pointer samples
 * only replace that transient value and request the single frame. The frame paints cached layer
 * rasters plus an overlay; pointerup validates and commits one immutable document, then publishes
 * one StudioUiState for panels. Cancel discards the preview. The route owner injects render inputs,
 * scene policy and design persistence, keeping this controller independent of W2-10's autosave.
 */
@Stable
internal class StageController(
    initial: StudioUiState,
    internal val renderInputs: (HistoryView, TextMeasurer) -> RenderInputs,
    internal val onDocumentCommit: (ScreenshotDesign) -> Unit,
    internal val onExternalIntent: (StudioIntent) -> Unit,
    internal val scope: CoroutineScope,
    internal val toasts: ToastState,
    internal val captureCache: CaptureImageCache,
    internal val scenes: SceneArranger? = null,
    internal val sceneInput: ((StudioUiState, dev.lingvo.shared.screenshots.doc.SceneSpec) -> dev.lingvo.shared.screenshots.scene.SceneInput)? = null,
    internal val sceneCommand: ((StudioUiState, dev.lingvo.shared.screenshots.doc.SceneSpec) -> EditCommand)? = null,
    private val now: () -> Long = { Date.now().toLong() },
    private val requestFrame: (() -> Unit) -> Int = { callback -> window.requestAnimationFrame { callback() } },
    private val cancelFrame: (Int) -> Unit = { window.cancelAnimationFrame(it) },
) : StudioController {
    override var uiState by mutableStateOf(initial)
        private set
    internal val history = DesignHistory(initial.document, initial.view.copy(selection = initial.selection))
    internal val selection = StudioSelection().apply { restore(initial.selection) }
    internal var captionBinding: CaptionBinding? = null
    internal var inlineEditing = false
    internal var viewport = StageViewport(800.0, 600.0, initial.board.canvas.width.toDouble(), initial.board.canvas.height.toDouble()).fit()
    init { uiState = initial.copy(zoomPercent = (viewport.zoom * 100).roundToInt()) }
    internal var host: HTMLElement? = null
    private var sceneCanvas: HTMLCanvasElement? = null
    private var overlayCanvas: HTMLCanvasElement? = null
    private var input: StageInput? = null
    private var observer: ResizeObserver? = null
    private var scheduler: FrameScheduler? = null
    private val cache = StageLayerCache(ScreenshotEngine.renderer())
    private val overlay = StageOverlay()
    private val announcer = StudioAnnouncer(now)
    internal var gesture: StageGesture? = null
    internal var temporaryHand = false
    private var split: StageSplit? = null
    internal var rasterZoom: Double? = null
    private val times = mutableListOf<Double>()
    private var serial = 0
    internal var clipboard: List<Layer> = emptyList()
    internal val commands get() = EditCommands(EditTarget(uiState.board.id, uiState.page.id)) { "editor-${now()}-${++serial}" }
    val listenerCount: Int get() = input?.listenerCount ?: 0
    val bitmapCount: Int get() = cache.size + captureCache.size + if (split != null) 2 else 0
    internal fun publish(message: String? = null) {
        uiState = uiState.copy(document = history.document, view = history.view, selection = selection.ids,
            canUndo = history.canUndo, canRedo = history.canRedo, zoomPercent = (viewport.zoom * 100).roundToInt(),
            announcement = message?.let { announcer.commit(it) } ?: uiState.announcement,
            inlineEditing = inlineEditing)
        captionBinding?.let { binding ->
            allLayers(uiState.layers).filterIsInstance<dev.lingvo.shared.screenshots.doc.TextLayer>()
                .find { it.base.id == binding.snapshot.layer.base.id }?.let(binding::updateLayer)
        }
        scheduler?.request()
    }
    internal fun updateState(change: (StudioUiState) -> StudioUiState) { uiState = change(uiState) }
    override fun dispatch(intent: StudioIntent) {
        if (captionPending(uiState.caption) && (intent is StudioIntent.Select || intent is StudioIntent.Slot ||
                intent is StudioIntent.Language || intent is StudioIntent.Device ||
                intent is StudioIntent.Action && intent.action.type in setOf(StudioActionType.UNDO, StudioActionType.REDO,
                    StudioActionType.SLOT, StudioActionType.LANGUAGE))) {
            updateState { it.copy(error = tr("studio.editor.stage_controller.save_or_cancel_this_caption_before", "Save or cancel this caption before leaving Canvas or changing the selection.")) }
            return
        }
        try { handleStudioIntent(intent) }
        catch (failure: IllegalArgumentException) {
            uiState = uiState.copy(error = tr("studio.editor.stage_controller.this_edit_would_make_the_design", "This edit would make the design invalid. Change the values and try again."))
        }
    }
    internal fun commit(command: EditCommand, label: String, nudge: NudgeKey? = null) {
        if (!uiState.editable) return
        val next = command.apply(history.document)
        if (history.commit(next, label, now(), nudge)) {
            onDocumentCommit(next)
            publish(label)
        }
    }
    internal fun setSelection(id: String, toggle: Boolean = false, extend: Boolean = false) {
        selection.selectFromTree(uiState.page, id, toggle, extend)
        history.switchView(history.view.copy(selection = selection.ids))
        publish()
    }
    fun setCaption(snapshot: CaptionSnapshot, fitter: CaptionFitPort, keys: CaptionKeysPort = KeysCaptionPort) {
        captionBinding?.dispose()
        captionBinding = CaptionBinding(snapshot, keys, fitter, { delay, callback -> window.setTimeout(callback, delay) },
            { window.clearTimeout(it) }) { caption ->
            if (caption.keyId == uiState.caption?.keyId && caption.locale == uiState.view.locale) {
                uiState = uiState.copy(caption = caption); cache.clear(); scheduler?.request()
            }
        }
        uiState = uiState.copy(caption = snapshot)
    }
    internal fun saveCaption() {
        val binding = captionBinding ?: return
        scope.launch {
            try {
                val receipt = binding.save() ?: return@launch
                inlineEditing = false
                showCaptionSavedToast(toasts, scope, receipt, { restored ->
                    if (uiState.caption?.keyId == restored.keyId && uiState.view.locale == restored.locale) {
                        uiState = uiState.copy(caption = restored); cache.clear(); scheduler?.request()
                    }
                }, { message -> uiState = uiState.copy(error = message) })
                publish()
            } catch (cancelled: kotlinx.coroutines.CancellationException) {
                throw cancelled
            } catch (failure: Exception) {
                uiState = uiState.copy(error = failure.message ?: tr("studio.editor.stage_controller.couldnt_save_this_caption_retry", "Couldn't save this caption. Retry."))
            }
        }
    }
    fun attach(element: HTMLElement) {
        if (host != null) detach()
        host = element
        sceneCanvas = element.querySelector("canvas.scene") as? HTMLCanvasElement
        overlayCanvas = element.querySelector("canvas.overlay") as? HTMLCanvasElement
        require(sceneCanvas != null && overlayCanvas != null) { "The canvas stage must contain scene and overlay canvases" } // i18n-ignore: internal contract diagnostic
        scheduler = FrameScheduler(requestFrame, cancelFrame, ::draw)
        fun resize() {
            val width = element.clientWidth.toDouble()
            val height = element.clientHeight.toDouble()
            if (width <= 0 || height <= 0) return
            viewport = viewport.resized(width, height, window.devicePixelRatio)
            listOfNotNull(sceneCanvas, overlayCanvas).forEach { canvas ->
                canvas.width = (width * viewport.pixelRatio).roundToInt(); canvas.height = (height * viewport.pixelRatio).roundToInt()
                canvas.style.width = "${width}px"; canvas.style.height = "${height}px"
            }
            input?.measure(); publish()
        }
        input = StageInput(this, element, overlayCanvas!!).also { it.attach() }
        observer = ResizeObserver { resize() }.also { it.observe(element) }
        resize(); element.focus()
    }
    fun detach() {
        input?.detach(); input = null
        observer?.disconnect(); observer = null
        scheduler?.dispose(); scheduler = null
        captionBinding?.dispose(); captionBinding = null
        split?.close(); split = null
        cache.clear(); captureCache.clear()
        listOfNotNull(sceneCanvas, overlayCanvas).forEach { it.width = 0; it.height = 0 }
        gesture = null; temporaryHand = false
        sceneCanvas = null; overlayCanvas = null; host = null
    }
    fun setPalette(accent: String, border: String) { host?.style?.setProperty("--studio-accent",accent); overlay.accent = accent; overlay.border = border; scheduler?.request() }
    fun inputsChanged() { cache.clear(); scheduler?.request() }
    fun flushFrame() { scheduler?.flushFrame() }
    internal fun redraw() { scheduler?.request() }
    internal fun focusStage() { host?.focus() }
    internal fun pageForPreview(): Page = uiState.page.copy(layers = mapLayers(uiState.layers) { layer ->
        gesture?.boxes?.get(layer.base.id)?.let { withResizedBox(layer, it) } ?: layer
    })
    internal fun bounds(page: Page = uiState.page.copy(layers = uiState.layers)): Rect? {
        val rects = allLayers(page.layers).filter { it.base.id in selection.ids }.map { EngineEditorGeometry.bounds(page, it) }
        if (rects.isEmpty()) return null
        val x=rects.minOf { it.x };val y=rects.minOf { it.y }
        return Rect(x,y,rects.maxOf { it.right }-x,rects.maxOf { it.bottom }-y)
    }
    internal fun pointerDown(point: Point, shift: Boolean, deep: Boolean, pan: Boolean, touch: Boolean) {
        if (captionPending(uiState.caption)) return
        val doc = viewport.toDocument(point)
        val bounds = bounds()
        val handle = if (uiState.editable && window.innerWidth >= 768 && !pan && bounds != null) overlay.hit(bounds,viewport,point,touch) else null
        if (handle != null) {
            gesture = StageGesture(if (handle.handle == null) "rotate" else "resize", doc, point, viewport, uiState.selected.associate { it.base.id to it.base.box },
                bounds!!, handle = handle.handle)
        } else if (pan || touch && EngineEditorGeometry.hitTest(uiState.layers,doc,4/viewport.zoom)==null) {
            gesture = StageGesture("pan",doc,point,viewport,emptyMap(),Rect(0.0,0.0,1.0,1.0))
        } else {
            val hit = EngineEditorGeometry.hitTest(uiState.layers,doc,4/viewport.zoom)
            if (hit !in selection.ids || shift || deep) selection.click(uiState.page.copy(layers=uiState.layers),doc,EngineEditorGeometry,shift,deep,viewport.zoom)
            history.switchView(history.view.copy(selection=selection.ids))
            publish()
            val selectedBounds=bounds()
            if (selectedBounds != null && uiState.editable && window.innerWidth >= 768 && uiState.selected.all { layer -> pathTo(uiState.layers, layer.base.id).none { it.base.locked } }) {
                gesture=StageGesture("move",doc,point,viewport,uiState.selected.associate { it.base.id to it.base.box },selectedBounds,
                    candidates=SnapCandidates.build(uiState.page.copy(layers=uiState.layers),uiState.board,selection.ids,
                        dev.lingvo.shared.screenshots.ScreenshotLooks.safeArea(uiState.board,
                            allLayers(uiState.layers).filterIsInstance<DeviceLayer>().firstOrNull()?.frame)))
            } else if (!touch) gesture=StageGesture("marquee",doc,point,viewport,emptyMap(),Rect(doc.x,doc.y,0.0,0.0))
        }
    }
    internal fun pointerMove(point: Point, shift: Boolean, alt: Boolean, command: Boolean) {
        val active=gesture ?: return
        val doc=viewport.toDocument(point)
        var delta=Point(doc.x-active.start.x,doc.y-active.start.y)
        if (active.kind=="pan") { viewport=active.viewport.panBy(Point(point.x-active.screen.x,point.y-active.screen.y));redraw();return }
        if (active.kind=="marquee") { active.marquee=Rect(minOf(active.start.x,doc.x),minOf(active.start.y,doc.y),kotlin.math.abs(delta.x),kotlin.math.abs(delta.y));redraw();return }
        when(active.kind) {
            "move" -> {
                if(shift) delta=if(kotlin.math.abs(delta.x)>=kotlin.math.abs(delta.y)) Point(delta.x,0.0) else Point(0.0,delta.y)
                val snapped=active.candidates?.move(active.bounds,delta,viewport.zoom,command)
                delta=snapped?.delta ?: delta;active.guides=snapped?.guides.orEmpty()
                active.boxes=moveBoxesInDocument(uiState.page.copy(layers = uiState.layers), active.original, delta)
            }
            "resize" -> {
                val handle=active.handle!!
                val single=uiState.selected.singleOrNull()
                val resized=resizeGesture(Box(active.bounds.x,active.bounds.y,active.bounds.w,active.bounds.h),handle,delta,
                    single is DeviceLayer || single is dev.lingvo.shared.screenshots.doc.ImageLayer,shift,alt,
                    uiState.selected.any { layer -> allLayers(listOf(layer)).any { it is DeviceLayer } })
                active.boxes=scaleSelection(active.original,active.bounds,resized)
            }
            "rotate" -> {
                val cx=active.bounds.x+active.bounds.w/2;val cy=active.bounds.y+active.bounds.h/2
                val degrees=(atan2(doc.y-cy,doc.x-cx)-atan2(active.start.y-cy,active.start.x-cx))*180/kotlin.math.PI
                val originalAngle = active.original.values.singleOrNull()?.rotation ?: 0.0
                val rotation=EngineEditorGeometry.snapAngle(originalAngle + degrees,shift) - originalAngle
                active.boxes=active.original.mapValues { (_,box)->
                    val radians=rotation*kotlin.math.PI/180;val x=box.x+box.w/2-cx;val y=box.y+box.h/2-cy
                    box.copy(x=cx+x*kotlin.math.cos(radians)-y*kotlin.math.sin(radians)-box.w/2,
                        y=cy+x*kotlin.math.sin(radians)+y*kotlin.math.cos(radians)-box.h/2,rotation=box.rotation+rotation)
                }
            }
        }
        redraw()
    }
    internal fun pointerUp(cancelled: Boolean, shift: Boolean) {
        val active=gesture ?: return
        gesture=null
        split?.close(); split=null
        if(!cancelled) {
            if(active.kind=="marquee") active.marquee?.let { selection.marquee(uiState.page.copy(layers=uiState.layers),it,EngineEditorGeometry,shift) }
            else if(active.boxes.isNotEmpty() && active.boxes!=active.original) applyStudioBoxes(active.boxes,
                tr("studio.editor.stage_controller.transformed_selected_layers","Transformed selected layers"))
        }
        publish()
    }
    private fun draw() {
        val scene=sceneCanvas ?: return;val target=overlayCanvas ?: return
        val start=window.performance.now()
        val ctx:dynamic=scene.getContext("2d");val top:dynamic=target.getContext("2d")
        ctx.setTransform(1,0,0,1,0,0);ctx.clearRect(0,0,scene.width,scene.height)
        val scale=viewport.zoom*viewport.pixelRatio
        ctx.setTransform(scale,0,0,scale,viewport.pan.x*viewport.pixelRatio,viewport.pan.y*viewport.pixelRatio)
        val preview=pageForPreview()
        val state=uiState.copy(document=uiState.document.copy(boards=uiState.document.boards.map { board ->
            if(board.id!=uiState.board.id)board else board.copy(mirrorRtl=false,pages=board.pages.map { if(it.id==preview.id)preview.copy(overrides=emptyMap())else it })
        }))
        val inputs=renderInputs(uiState.view,CanvasMeasurer(ctx))
        // Backdrop-dependent blends must see the actual scene instead of isolated layer bitmaps.
        if (allLayers(preview.layers).any { it.base.blend != dev.lingvo.shared.screenshots.doc.BlendMode.NORMAL }) {
            split?.close(); split = null
            val adapter = CanvasSurface(ctx, state.view.locale)
            val surface = object : dev.lingvo.shared.screenshots.render.Surface by adapter {
                override val width = state.board.canvas.width
                override val height = state.board.canvas.height
            }
            ScreenshotEngine.renderer().renderSlot(state.document, state.board.id, state.view.slotId, inputs, surface)
            overlay.draw(top, viewport, bounds(preview), uiState.editable, gesture?.marquee, gesture?.guides.orEmpty(), null)
            return
        }
        fun paint(context: dynamic, layers: List<Layer>) {
            context.setTransform(scale,0,0,scale,viewport.pan.x*viewport.pixelRatio,viewport.pan.y*viewport.pixelRatio)
            layers.forEach { layer ->
                if (!layer.base.hidden && !(inlineEditing && uiState.caption?.layer?.base?.id == layer.base.id)) {
                    val original = if (gesture?.kind == "move" && layer.base.id in selection.ids) uiState.layers.find { it.base.id == layer.base.id } else null
                    val raster = cache.layer(if (original != null) uiState else state, original ?: layer, inputs, rasterZoom ?: scale)
                    val dx = if (original != null) layer.base.box.x - original.base.box.x else 0.0
                    val dy = if (original != null) layer.base.box.y - original.base.box.y else 0.0
                    context.drawImage(raster.bitmap,raster.bounds.x+dx,raster.bounds.y+dy,raster.bounds.w,raster.bounds.h)
                }
            }
        }
        if (gesture?.kind == "move") {
            val indices = uiState.layers.indices.filter { index -> allLayers(listOf(uiState.layers[index])).any { it.base.id in selection.ids } }
            if (indices.isNotEmpty()) {
                val first = indices.first(); val last = indices.last()
                if (split == null) split = StageSplit(scene.width, scene.height) { context, side ->
                    paint(context, if (side < 0) uiState.layers.take(first) else uiState.layers.drop(last + 1))
                }
                split!!.draw(ctx) { paint(it, preview.layers.subList(first, last + 1)) }
            } else paint(ctx, preview.layers)
        } else paint(ctx, preview.layers)
        val elapsed=window.performance.now()-start;times+=elapsed;if(times.size>120)times.removeAt(0)
        val debug=if(window.location.search.contains("studioDebug=1")) {
            val sorted=times.sorted();"p50 ${sorted[sorted.size/2]} ms · p95 ${sorted[(sorted.size*.95).toInt().coerceAtMost(sorted.lastIndex)]} ms · ${cache.size}"
        }else null
        overlay.draw(top,viewport,bounds(preview),uiState.editable,gesture?.marquee,gesture?.guides.orEmpty(),debug)
    }
}

/** Only this plain object changes during a gesture; it is deliberately absent from Compose state. */
internal data class StageGesture(
    val kind:String,val start:Point,val screen:Point,val viewport:StageViewport,val original:Map<String,Box>,val bounds:Rect,
    val handle:Handle?=null,val candidates:SnapCandidates?=null,var boxes:Map<String,Box> = original,
    var marquee:Rect?=null,var guides:List<Guide> = emptyList(),
)
